import { useModalMeta } from "@/shared/atoms/useModal";
import { useState } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { guidGenerator } from "../../shared/utils/helpers";
import Alert from "../Alert/Alert";
import BooleanWithLabel from "../Boolean/BooleanWithLabel";
import Button from "../Button/Button";
import Checkbox from "../Checkbox/Checkbox";
import IconToolTip from "../Icon/IconToolTip";
import Textfield from "../Input/Textfield";
import LanguageTabs, {
  LanguageTabContext,
  useTabLanguages,
} from "../Input/LanguageTabs";
import ProductModalAdvance from "../Menu/ProductModalAdvance";
import ProductModalStandard from "../Menu/ProductModalStandard";
import useProductForm from "../Menu/useProductForm";
import Modal, { ModalProps } from "../Modal/Modal";
import ModalTitle from "../Modal/ModalTitle";

export interface ConfirmationProps extends ModalProps {
  isPending?: boolean;
  defaultValue?: any;
  onSubmit: (value: any) => void;
  title?: string;
  isError?: boolean;
  edit?: boolean;
  parentId?: number;
}

// Products are created/edited on /menu/product; this modal remains for flows
// that must not leave the current screen (e.g. adding a product from the POS).
const MenuProductModal = ({ onExit, ...props }: ModalProps) => {
  const [{ defaultValue, isEdit, parentId, callback }] = useModalMeta();
  const [isStandardSettings, setIsStandardSettings] = useState(true);
  const { t } = useTranslation();
  const [another, setAnother] = useState(false);
  const languages = useTabLanguages();
  const [language, setLanguage] = useState(languages[0]?.key);

  const {
    methods,
    fields,
    setFields,
    labels,
    vats,
    save,
    refetch,
    productUpdate,
    isPending,
    isError,
  } = useProductForm({ defaultValue, parentId, isEdit });
  const { register, handleSubmit, errors, reset } = methods;

  const onFormSubmit = (data) =>
    save(data).then(() => {
      if (isEdit) return onExit();
      callback && callback(refetch);
      reset();
      !another && onExit();
    });

  return (
    <Modal
      {...props}
      fullHeight
      width="95rem"
      onExit={() => {
        productUpdate.reset();
        onExit();
      }}
    >
      <div className="p-[2rem]">
        <ModalTitle>
          {isEdit ? t("menu.productEdit") : t("menu.productCreate")}
        </ModalTitle>
        {isError && (
          <div className="mb-12">
            <Alert>{t("errorMessage")}</Alert>
          </div>
        )}
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onFormSubmit)}>
            <Textfield
              readOnly
              hidden
              value={defaultValue?.code || guidGenerator()}
              name="code"
              error={errors?.code?.message}
              ref={register({})}
            />
            <div className="mb-8 flex items-center gap-8">
              <BooleanWithLabel
                checked={fields.is_active ?? false}
                onChange={(e) =>
                  setFields((draft) => {
                    draft.is_active = e.target.checked;
                  })
                }
              >
                {t("menu.isActive")}
              </BooleanWithLabel>

              <BooleanWithLabel
                checked={fields.is_18_plus ?? false}
                onChange={(e) =>
                  setFields((draft) => {
                    draft.is_18_plus = e.target.checked;
                  })
                }
              >
                {t("menu.is18plus")}
              </BooleanWithLabel>
              <div
                onClick={() => setIsStandardSettings(!isStandardSettings)}
                className="ml-auto"
              >
                <IconToolTip
                  text={
                    isStandardSettings
                      ? t("menu.advanceSettings")
                      : t("menu.standardSettings")
                  }
                  variants={isStandardSettings ? "gear" : "reply"}
                  className="fill-primary"
                />
              </div>
            </div>

            {isStandardSettings && (
              <LanguageTabs
                selected={language}
                onChange={setLanguage}
                className="mb-8"
              />
            )}

            <LanguageTabContext.Provider value={language ?? null}>
              <ProductModalStandard
                defaultValue={defaultValue}
                fields={fields}
                setFields={setFields}
                labels={labels}
                vats={vats}
                isShown={isStandardSettings}
              />
            </LanguageTabContext.Provider>

            <ProductModalAdvance
              defaultValue={defaultValue}
              isShown={!isStandardSettings}
            />

            <div className="mt-10">
              <div className="flex flex-wrap items-center">
                <div className="p-2">
                  <Button type="submit" isPending={isPending}>
                    {t("save")}
                  </Button>
                </div>

                {!isEdit && (
                  <div className="ml-[1rem]">
                    <Checkbox
                      onChange={(e) => setAnother(e.target.checked)}
                      active={another}
                    >
                      {t("createAnotherOne")}
                    </Checkbox>
                  </div>
                )}
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </Modal>
  );
};

export default MenuProductModal;
